import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { FlexRow } from '@repo/design-system/components/layout/flex-row';
import { useHostMode, useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { clearExploreActionState, selectAutoFocusSearch, selectIsCompactViewport, selectSidebarOpen, selectSidebarView, setActiveChecklistSubcategory, setActiveCustomChecklistId, setActiveHeroActionId, setAskAnythingResult, setAutoFocusSearch, setHeroActionTriggerPinId, setSharedChecklistPayload, setSidebarOpen, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectCurrentMapInfo, selectExternalMapId, selectFilterState, selectTitleName, setSearchTerm } from '@repo/store/maps/slices/mapData';
import { CUSTOM_PINS_CATEGORY_ID, groupCategories } from '@repo/store/maps/utils/mapData';
import { HostMode, HostType } from '@repo/types/host';
import type { MapInitializationData } from '@repo/types/maps/map-types';
import type React from 'react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChecklistEntryButton } from '../checklist/checklist-entry-button';
import { ChecklistSidebarView } from '../checklist/checklist-sidebar-view';
import { CustomChecklistSidebarView } from '../checklist/custom-checklist-sidebar-view';
import { SharedChecklistSidebarView } from '../checklist/shared-checklist-sidebar-view';
import { useChecklistNavigation } from '../checklist/use-checklist-navigation';
import { MapsAuthModal } from '../components/maps-auth-modal';
import { AskAnythingSidebarView } from '../explore/ask-anything-sidebar-view';
import { ExploreSidebarView } from '../explore/explore-sidebar-view';
import { useSessionHistory } from '../game-map/hooks/useSessionHistory';
import { useSharedChecklistUrlInitializer } from '../game-map/hooks/useSharedChecklistUrlInitializer';
import { HeroActionSidebarView } from '../location-info/hero-action-sidebar-view';
import { DebugModeToggle } from '../map-controls/debug-mode-toggle';
import { MapDetailsModal } from '../map-details-modal/map-details-modal';
import { MapDiscoveryProgress } from '../map-discovery-progress';
import { HeatmapEventsSection } from './heatmap-events-section';
import { MapCategorySection } from './map-category-section';
import { MapFilters } from './map-filters';
import { MapRegionFilters } from './map-region-filters';
import { MapSearch } from './map-search';
import { MapSearchResults } from './map-search-results';
import { MapSelector } from './map-selector';
import { MapSidebarAdShell } from './map-sidebar-ad-shell';
import { ResetFoundPins } from './reset-found-pins';
import { SessionHistorySection } from './session-history-section';
import { SessionHistoryView } from './session-history-view';
const MIN_WIDTH = 300;
const MAX_WIDTH = 520;
const DEFAULT_WIDTH = 355;
const COMPACT_WIDTH = 300;
const SEARCH_DEBOUNCE_MS = 400;
const TRANSITION_DURATION_MS = 200;
const SIDEBAR_CONTENT_INSET = '16';
const SIDEBAR_SCROLLBAR_INSET = '4';
const SIDEBAR_SCROLLBAR_SIZE = '6';
const SIDEBAR_SECTION_PADDING_RIGHT = '12';
const SIDEBAR_FIXED_SECTION_PADDING_RIGHT = '[18px]';
const sidebarStyles = css({
  backgroundColor: 'wemod.elevated.100',
  display: 'flex',
  flexDirection: 'column',
  margin: '0',
  zIndex: 4,
  userSelect: 'none',
  height: 'full',
  transition: '[transform 0.2s cubic-bezier(0.33, 0, 0.2, 1), width 0.2s cubic-bezier(0.33, 0, 0.2, 1)]',
  overflow: 'hidden',
  flexShrink: 0,
  order: -1,
  containerType: 'inline-size'
});
const resizeHandleStyles = css({
  position: 'absolute',
  top: 'none',
  right: 'none',
  width: '[4px]',
  height: 'full',
  cursor: 'col-resize',
  backgroundColor: 'transparent',
  zIndex: '[12]',
  _hover: {
    backgroundColor: 'whiteAlpha.20'
  }
});
const scrollableStyles = css({
  flex: '[1]',
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  overflowX: 'hidden',
  width: 'full',
  scrollbarGutter: '[stable]',
  scrollbarWidth: '[thin]',
  scrollbarColor: '[rgba(255,255,255,0.25) transparent]',
  '&::-webkit-scrollbar': {
    width: SIDEBAR_SCROLLBAR_SIZE
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'whiteAlpha.20',
    borderRadius: 'full'
  }
});
const scrollContentInsetStyles = css.raw({
  display: 'flex',
  flexDirection: 'column',
  gap: '8',
  width: 'full',
  paddingRight: SIDEBAR_SECTION_PADDING_RIGHT
});
const sidebarFixedSectionInsetStyles = css.raw({
  width: 'full',
  paddingRight: SIDEBAR_FIXED_SECTION_PADDING_RIGHT
});
const sidebarMainContentStyles = (isCollapsed: boolean, hasSidebarAd: boolean) => css.raw({
  transition: '[opacity 0.2s ease-in-out, transform 0.2s cubic-bezier(0.33, 0, 0.2, 1)]',
  opacity: isCollapsed ? 0 : 1,
  margin: 'none',
  pointerEvents: isCollapsed ? 'none' : 'auto',
  paddingTop: SIDEBAR_CONTENT_INSET,
  paddingBottom: hasSidebarAd ? 'none' : SIDEBAR_CONTENT_INSET,
  paddingLeft: SIDEBAR_CONTENT_INSET,
  paddingRight: SIDEBAR_SCROLLBAR_INSET,
  flex: '[1]',
  minHeight: '0'
});
export const MapSideBar = t0 => {
  const $ = _c(129);
  const {
    providedMapData,
    sidebarAd
  } = t0;
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(_temp);
  const isCompactViewport = useAppSelector(selectIsCompactViewport);
  const autoFocusSearch = useAppSelector(selectAutoFocusSearch);
  const sidebarOpen = useAppSelector(selectSidebarOpen);
  const sidebarView = useAppSelector(selectSidebarView);
  const [isCollapsed, setIsCollapsed] = useState(!sidebarOpen);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const [ghostPosition, setGhostPosition] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMapDetailsModalOpen, setIsMapDetailsModalOpen] = useState(false);
  const hostMode = useHostMode();
  const hostType = useHostType();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  const externalMapId = useAppSelector(selectExternalMapId);
  const sidebarRef = useRef(null);
  const searchInputRef = useRef(null);
  const {
    groupedCategories
  } = useAppSelector(_temp2);
  const filters = useAppSelector(selectFilterState);
  const analytics = useAnalytics();
  const [inputValue, setInputValue] = useState(filters.searchTerm);
  const debounceRef = useRef(null);
  const {
    hideSessionTrail,
    setTimelineVisible
  } = useSessionHistory();
  const currentMapInfo = useAppSelector(selectCurrentMapInfo);
  const titleName = useAppSelector(selectTitleName);
  const isChecklistPublished = currentMapInfo?.isChecklistPublished ?? false;
  const isInActiveDevelopment = currentMapInfo?.isInActiveDevelopment ?? providedMapData.mapConfig.isInActiveDevelopment;
  const {
    enterChecklist: enterChecklistFilters,
    exitChecklist: exitChecklistFilters
  } = useChecklistNavigation();
  let t2;
  if ($[1] !== dispatch || $[2] !== enterChecklistFilters) {
    t2 = () => {
      enterChecklistFilters();
      dispatch(setActiveChecklistSubcategory(null));
      dispatch(setSidebarView("checklist"));
    };
    $[1] = dispatch;
    $[2] = enterChecklistFilters;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const enterChecklist = t2;
  let t3;
  if ($[4] !== dispatch || $[5] !== exitChecklistFilters) {
    t3 = () => {
      exitChecklistFilters();
      dispatch(setActiveChecklistSubcategory(null));
      dispatch(setSidebarView("default"));
    };
    $[4] = dispatch;
    $[5] = exitChecklistFilters;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const exitChecklist = t3;
  let t4;
  if ($[7] !== dispatch) {
    t4 = () => {
      dispatch(setSidebarView("default"));
      dispatch(clearExploreActionState());
    };
    $[7] = dispatch;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const exitExplore = t4;
  let t5;
  if ($[9] !== dispatch) {
    t5 = () => {
      dispatch(setSidebarView("checklist"));
      dispatch(setActiveCustomChecklistId(null));
    };
    $[9] = dispatch;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const exitCustomChecklist = t5;
  let t6;
  if ($[11] !== dispatch) {
    t6 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setSharedChecklistPayload(null));
      if (typeof window !== "undefined") {
        const url = new URL(window.location.href);
        if (url.searchParams.has("cl")) {
          url.searchParams.delete("cl");
          window.history.replaceState(null, "", url.toString());
        }
      }
    };
    $[11] = dispatch;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const exitSharedChecklist = t6;
  useSharedChecklistUrlInitializer();
  let t7;
  if ($[13] !== dispatch) {
    t7 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setActiveHeroActionId(null));
      dispatch(setHeroActionTriggerPinId(null));
    };
    $[13] = dispatch;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const exitHeroAction = t7;
  let t8;
  if ($[15] !== dispatch) {
    t8 = () => {
      dispatch(setSidebarView("default"));
      dispatch(setAskAnythingResult(null));
    };
    $[15] = dispatch;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const exitAskAnything = t8;
  let t9;
  if ($[17] !== providedMapData.mapData) {
    t9 = groupCategories(providedMapData.mapData, {});
    $[17] = providedMapData.mapData;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const defaultGroupedCategories = t9;
  let t10;
  if ($[19] !== analytics || $[20] !== dispatch || $[21] !== externalMapId || $[22] !== hostType) {
    t10 = e => {
      const newValue = e.target.value;
      setInputValue(newValue);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (newValue.trim()) {
          analytics.event("game_map_search", {
            mapId: externalMapId,
            terms: newValue,
            platform: hostType
          });
        }
        dispatch(setSearchTerm(newValue));
      }, SEARCH_DEBOUNCE_MS);
    };
    $[19] = analytics;
    $[20] = dispatch;
    $[21] = externalMapId;
    $[22] = hostType;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSearchChange = t10;
  let t11;
  if ($[24] !== analytics || $[25] !== dispatch || $[26] !== externalMapId || $[27] !== hostType || $[28] !== sidebarOpen) {
    t11 = () => {
      dispatch(setSidebarOpen(!sidebarOpen));
      analytics.event("game_map_resize_sidebar", {
        mapId: externalMapId,
        collapsed: sidebarOpen,
        platform: hostType
      });
    };
    $[24] = analytics;
    $[25] = dispatch;
    $[26] = externalMapId;
    $[27] = hostType;
    $[28] = sidebarOpen;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const toggleCollapse = t11;
  let t12;
  if ($[30] !== width) {
    t12 = e_0 => {
      e_0.preventDefault();
      setIsResizing(true);
      setGhostPosition(width);
    };
    $[30] = width;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  const handleMouseDown = t12;
  let t13;
  if ($[32] !== isCollapsed || $[33] !== isCompactViewport || $[34] !== isResizing) {
    t13 = e_1 => {
      if (!isResizing || isCollapsed) {
        return;
      }
      const newWidth = e_1.clientX;
      const maxWidth = isCompactViewport ? COMPACT_WIDTH : MAX_WIDTH;
      if (newWidth >= MIN_WIDTH && newWidth <= maxWidth) {
        setGhostPosition(newWidth);
      }
    };
    $[32] = isCollapsed;
    $[33] = isCompactViewport;
    $[34] = isResizing;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const handleMouseMove = t13;
  let t14;
  if ($[36] !== ghostPosition || $[37] !== width) {
    t14 = () => {
      if (ghostPosition !== null && ghostPosition !== width) {
        setIsTransitioning(true);
        setWidth(ghostPosition);
        setTimeout(() => {
          setIsTransitioning(false);
        }, TRANSITION_DURATION_MS);
      }
      setGhostPosition(null);
      setIsResizing(false);
    };
    $[36] = ghostPosition;
    $[37] = width;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const handleMouseUp = t14;
  let t15;
  let t16;
  if ($[39] !== handleMouseMove || $[40] !== handleMouseUp || $[41] !== isResizing) {
    t15 = () => {
      if (isResizing) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    };
    t16 = [isResizing, handleMouseMove, handleMouseUp];
    $[39] = handleMouseMove;
    $[40] = handleMouseUp;
    $[41] = isResizing;
    $[42] = t15;
    $[43] = t16;
  } else {
    t15 = $[42];
    t16 = $[43];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[44] !== filters.searchTerm) {
    t17 = () => {
      setInputValue(filters.searchTerm);
    };
    t18 = [filters.searchTerm];
    $[44] = filters.searchTerm;
    $[45] = t17;
    $[46] = t18;
  } else {
    t17 = $[45];
    t18 = $[46];
  }
  useEffect(t17, t18);
  let t19;
  if ($[47] !== isCollapsed || $[48] !== sidebarOpen) {
    t19 = () => {
      let timer;
      if (sidebarOpen && isCollapsed) {
        setIsTransitioning(true);
        setIsCollapsed(false);
        timer = setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION_MS);
      } else {
        if (!sidebarOpen && !isCollapsed) {
          setIsTransitioning(true);
          setIsCollapsed(true);
          timer = setTimeout(() => setIsTransitioning(false), TRANSITION_DURATION_MS);
        }
      }
      return () => clearTimeout(timer);
    };
    $[47] = isCollapsed;
    $[48] = sidebarOpen;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== sidebarOpen) {
    t20 = [sidebarOpen];
    $[50] = sidebarOpen;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  useEffect(t19, t20);
  let t21;
  let t22;
  if ($[52] !== autoFocusSearch || $[53] !== dispatch) {
    t21 = () => {
      if (autoFocusSearch) {
        dispatch(setAutoFocusSearch(false));
        setTimeout(() => searchInputRef.current?.focus(), TRANSITION_DURATION_MS);
      }
    };
    t22 = [autoFocusSearch, dispatch];
    $[52] = autoFocusSearch;
    $[53] = dispatch;
    $[54] = t21;
    $[55] = t22;
  } else {
    t21 = $[54];
    t22 = $[55];
  }
  useEffect(t21, t22);
  let t23;
  let t24;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    t24 = [];
    $[56] = t23;
    $[57] = t24;
  } else {
    t23 = $[56];
    t24 = $[57];
  }
  useEffect(t23, t24);
  const effectiveWidth = isCompactViewport ? Math.min(width, COMPACT_WIDTH) : width;
  const currentWidth = effectiveWidth;
  let t25;
  if ($[58] !== isLoggedIn) {
    t25 = () => {
      if (!isLoggedIn) {
        setIsLoginModalOpen(true);
      } else {
        setIsMapDetailsModalOpen(true);
      }
    };
    $[58] = isLoggedIn;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  const handleMapDiscoveryProgressClick = t25;
  if (hostType === HostType.WEB && hostMode === HostMode.EMBED) {
    return null;
  }
  const categories = groupedCategories?.length ? groupedCategories : defaultGroupedCategories;
  const t26 = isCollapsed || isResizing || isTransitioning ? "absolute" : "relative";
  const t27 = `${currentWidth}px`;
  const t28 = isCollapsed ? "translate3d(-100%, 0, 0)" : "translate3d(0, 0, 0)";
  let t29;
  if ($[60] !== t26 || $[61] !== t27 || $[62] !== t28) {
    t29 = {
      position: t26,
      width: t27,
      transform: t28
    };
    $[60] = t26;
    $[61] = t27;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== handleMouseDown || $[65] !== isCollapsed) {
    t30 = !isCollapsed && <div className={resizeHandleStyles} onMouseDown={handleMouseDown} />;
    $[64] = handleMouseDown;
    $[65] = isCollapsed;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== dispatch || $[68] !== hideSessionTrail || $[69] !== setTimelineVisible || $[70] !== sidebarView) {
    t31 = sidebarView === "sessionHistory" && <SessionHistoryView onBack={() => {
      dispatch(setSidebarView("default"));
      hideSessionTrail();
      setTimelineVisible(false);
    }} />;
    $[67] = dispatch;
    $[68] = hideSessionTrail;
    $[69] = setTimelineVisible;
    $[70] = sidebarView;
    $[71] = t31;
  } else {
    t31 = $[71];
  }
  let t32;
  if ($[72] !== exitChecklist || $[73] !== sidebarView) {
    t32 = sidebarView === "checklist" && <ChecklistSidebarView onBack={exitChecklist} />;
    $[72] = exitChecklist;
    $[73] = sidebarView;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== exitExplore || $[76] !== sidebarView) {
    t33 = sidebarView === "explore" && <ExploreSidebarView onBack={exitExplore} />;
    $[75] = exitExplore;
    $[76] = sidebarView;
    $[77] = t33;
  } else {
    t33 = $[77];
  }
  let t34;
  if ($[78] !== exitAskAnything || $[79] !== sidebarView) {
    t34 = sidebarView === "askAnything" && <AskAnythingSidebarView onBack={exitAskAnything} />;
    $[78] = exitAskAnything;
    $[79] = sidebarView;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== exitCustomChecklist || $[82] !== sidebarView) {
    t35 = sidebarView === "customChecklist" && <CustomChecklistSidebarView onBack={exitCustomChecklist} />;
    $[81] = exitCustomChecklist;
    $[82] = sidebarView;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== exitSharedChecklist || $[85] !== sidebarView) {
    t36 = sidebarView === "sharedChecklist" && <SharedChecklistSidebarView onBack={exitSharedChecklist} />;
    $[84] = exitSharedChecklist;
    $[85] = sidebarView;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] !== exitHeroAction || $[88] !== sidebarView) {
    t37 = sidebarView === "heroAction" && <HeroActionSidebarView onBack={exitHeroAction} />;
    $[87] = exitHeroAction;
    $[88] = sidebarView;
    $[89] = t37;
  } else {
    t37 = $[89];
  }
  let t38;
  if ($[90] !== analytics || $[91] !== categories || $[92] !== currentMapInfo?.name || $[93] !== dispatch || $[94] !== enterChecklist || $[95] !== externalMapId || $[96] !== filters.searchTerm || $[97] !== handleMapDiscoveryProgressClick || $[98] !== handleSearchChange || $[99] !== hostType || $[100] !== inputValue || $[101] !== isChecklistPublished || $[102] !== isCollapsed || $[103] !== isInActiveDevelopment || $[104] !== isLoginModalOpen || $[105] !== isMapDetailsModalOpen || $[106] !== sidebarAd || $[107] !== sidebarView || $[108] !== t || $[109] !== titleName || $[110] !== toggleCollapse) {
    t38 = sidebarView === "default" && <FlexColumn width="full" height="full" className={css.raw({
      minHeight: "0"
    })}><FlexColumn gap="8" width="full" height="full" className={sidebarMainContentStyles(isCollapsed, Boolean(sidebarAd))}><FlexRow gap="8" align="center" width="full" className={css.raw({
          flexShrink: 0
        }, sidebarFixedSectionInsetStyles)}><IconButton onClick={toggleCollapse} color="secondary" size="lg" aria-label={t(_temp3)}><Icon icon="right_panel_open" decorative={true} /></IconButton><MapSearch value={inputValue} onChange={handleSearchChange} inputRef={searchInputRef} /></FlexRow><div className={scrollableStyles}><div className={css(scrollContentInsetStyles)}><MapDiscoveryProgress onClick={handleMapDiscoveryProgressClick} showActiveDevelopmentMessage={isInActiveDevelopment} /><MapSelector isCollapsed={isCollapsed} /><MapFilters /><SessionHistorySection onClick={() => {
              dispatch(setSidebarView("sessionHistory"));
              analytics.event("game_map_timeline_open", {
                mapId: externalMapId,
                mapName: currentMapInfo?.name,
                titleName,
                platform: hostType
              });
            }} />{isChecklistPublished && <ChecklistEntryButton onClick={enterChecklist} />}<div className={css({
              borderBottom: "[1px solid]",
              borderColor: "whiteAlpha.5"
            })} />{inputValue.trim() || filters.searchTerm.trim() ? <MapSearchResults /> : <FlexColumn gap="12" width="full"><MapRegionFilters />{categories.map(category => <Fragment key={category.externalId}>{category.externalId === CUSTOM_PINS_CATEGORY_ID && <HeatmapEventsSection searchTerm={inputValue || filters.searchTerm} />}<MapCategorySection category={category} searchTerm={inputValue || filters.searchTerm} /></Fragment>)}</FlexColumn>}<FlexColumn justify="center" align="center" className={css.raw({
              padding: "12"
            })}><ResetFoundPins /></FlexColumn></div></div><div className={css(sidebarFixedSectionInsetStyles)}><DebugModeToggle /></div><MapsAuthModal isOpen={isLoginModalOpen} onOpenChange={details => setIsLoginModalOpen(details.open)} trigger="discovery-progress" size="sm" /><MapDetailsModal isOpen={isMapDetailsModalOpen} onOpenChange={details_0 => setIsMapDetailsModalOpen(details_0.open)} /></FlexColumn>{sidebarAd && <div className={css({
        flexShrink: 0,
        width: "full"
      })}><MapSidebarAdShell>{sidebarAd}</MapSidebarAdShell></div>}</FlexColumn>;
    $[90] = analytics;
    $[91] = categories;
    $[92] = currentMapInfo?.name;
    $[93] = dispatch;
    $[94] = enterChecklist;
    $[95] = externalMapId;
    $[96] = filters.searchTerm;
    $[97] = handleMapDiscoveryProgressClick;
    $[98] = handleSearchChange;
    $[99] = hostType;
    $[100] = inputValue;
    $[101] = isChecklistPublished;
    $[102] = isCollapsed;
    $[103] = isInActiveDevelopment;
    $[104] = isLoginModalOpen;
    $[105] = isMapDetailsModalOpen;
    $[106] = sidebarAd;
    $[107] = sidebarView;
    $[108] = t;
    $[109] = titleName;
    $[110] = toggleCollapse;
    $[111] = t38;
  } else {
    t38 = $[111];
  }
  let t39;
  if ($[112] !== t29 || $[113] !== t30 || $[114] !== t31 || $[115] !== t32 || $[116] !== t33 || $[117] !== t34 || $[118] !== t35 || $[119] !== t36 || $[120] !== t37 || $[121] !== t38) {
    t39 = <div ref={sidebarRef} className={sidebarStyles} style={t29}>{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}{t38}</div>;
    $[112] = t29;
    $[113] = t30;
    $[114] = t31;
    $[115] = t32;
    $[116] = t33;
    $[117] = t34;
    $[118] = t35;
    $[119] = t36;
    $[120] = t37;
    $[121] = t38;
    $[122] = t39;
  } else {
    t39 = $[122];
  }
  let t40;
  if ($[123] !== ghostPosition || $[124] !== isResizing) {
    t40 = isResizing && ghostPosition !== null && <div id="ghost-resize-indicator" className={css({
      position: "absolute",
      height: "full",
      width: "[4px]",
      backgroundColor: "whiteAlpha.50",
      zIndex: 1,
      pointerEvents: "none"
    })} style={{
      left: `${ghostPosition}px`
    }} />;
    $[123] = ghostPosition;
    $[124] = isResizing;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  let t41;
  if ($[126] !== t39 || $[127] !== t40) {
    t41 = <>{t39}{t40}</>;
    $[126] = t39;
    $[127] = t40;
    $[128] = t41;
  } else {
    t41 = $[128];
  }
  return t41;
};
function _temp(state) {
  return state.auth.isAuthenticated;
}
function _temp2(state_0) {
  return state_0.mapData;
}
function _temp3($) {
  return $.collapse_sidebar;
}